<script setup lang="ts">

    const props = withDefaults(defineProps<{
        step?: number
        title?: string
        subtitle?: string
        background?: boolean
        animate?: boolean
        pathSelected?: boolean
    }>(), {
        step: 0,
        title: '',
        subtitle: '',
    });

    const emit = defineEmits<{(event: 'next', value: number)}>();

    const ENTRY_DELAY = 100;
    const EXIT_DELAY = 1000;
    const STEP_MIDDLE = 0.5;
    const STEP_NEXT = 1;
    const STEP_ALREADY_SELECTED = 2;
    const WAITING_DELAY = 2000;

    const showElement = ref(false);
    const exitElement = ref(false);

    const handleExitStep = ():void => {
        exitElement.value = true;

        if (!props.animate) {
            emit('next', props.step + STEP_NEXT);
            return;
        }

        emit('next', props.step + STEP_MIDDLE);

        setTimeout(() => {
            emit('next', props.pathSelected ? (props.step + STEP_ALREADY_SELECTED) : (props.step + STEP_NEXT));
        }, EXIT_DELAY);
    };

    const resetAnimationState = ():void => {
        showElement.value = false;
        exitElement.value = false;

        // Trigger the "showElement" transition again after a brief delay
        setTimeout(() => {
            showElement.value = true;
        }, ENTRY_DELAY);
    };

    onMounted(() => {
        resetAnimationState();

        setTimeout(() => {
            handleExitStep();
        }, WAITING_DELAY);
    });
</script>

<template>
    <div
        :class="{
            'slide-bottom-up': showElement && !exitElement && animate,
            'slide-out': exitElement && animate,
            'slide-show': showElement && !exitElement && !animate,
            'greetings-card--background': background
        }"
        class="greetings-card"
    >
        <div class="greetings-card__text">
            <h2
                v-if="title"
                class="greetings-card__title"
            >
                {{ title }}
            </h2>
            <span
                v-if="subtitle"
                class="greetings-card__subtitle"
            >
                {{ subtitle }}
            </span>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.greetings-card {
    position: absolute;
    display: flex;
    overflow: hidden;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    block-size: 100%;
    gap: var(--spacing-07);
    inline-size: 100%;
    inset-block-start: 0;
    transform: translateY(100%);
    transition: all 0.75s ease-in-out;

    h2 {
        font-size: var(--font-h2);
        padding-inline: var(--spacing-03);
        text-align: center;

        @include min-width(md) {
            font-size: var(--font-h1);
        }
    }

    &--background {
        background-color: var(--background-primary);
    }

    &__subtitle {
        font-family: var(--font-secondary);
        font-size: var(--font-h3);
        font-weight: var(--font-weight-medium);
        line-height: 1.2;
        padding-inline: var(--spacing-03);
        text-align: center;
    }

    &__text {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--spacing-03);
        max-inline-size: 400px;
    }

    &.slide-show {
        transform: translateY(0);
        transition: none;
    }

    &.slide-bottom-up {
        transform: translateY(0);
    }

    &.slide-out {
        transform: translateY(-100%);
    }
}
</style>
